import { useEffect, useRef, useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Send, User, Headphones, Paperclip, X, Download, Play } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { AdminTicket, AdminTicketThread, AttachmentMeta, ReplyAttachment, TicketStatus } from '@/types';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: 'open',     label: 'Open' },
  { value: 'pending',  label: 'Pending' },
  { value: 'closed',   label: 'Closed' },
  { value: 'resolved', label: 'Resolved' },
];

const STATUS_BADGE: Record<string, string> = {
  open:     'bg-blue-100 text-blue-700',
  pending:  'bg-yellow-100 text-yellow-700',
  closed:   'bg-gray-100 text-gray-600',
  resolved: 'bg-green-100 text-green-700',
};

const ALLOWED_MIME: Record<string, boolean> = {
  'image/png': true, 'image/jpeg': true, 'image/gif': true, 'image/webp': true,
  'application/pdf': true,
  'application/vnd.ms-excel': true,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': true,
  'application/msword': true,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': true,
};

function relativeTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d}d ago`;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Media lightbox ───────────────────────────────────────────────────────────

function MediaLightbox({ att, blobUrl, onClose }: {
  att: AttachmentMeta;
  blobUrl: string;
  onClose: () => void;
}) {
  const isVideo = att.mime_type.startsWith('video/');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleDownload() {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = att.file_name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      onClick={onClose}
    >
      <div
        className="relative flex flex-col items-center"
        style={{ maxWidth: '90vw', maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/60 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        {/* Media */}
        {isVideo ? (
          <video
            src={blobUrl}
            controls
            autoPlay
            className="rounded-lg"
            style={{ maxWidth: '85vw', maxHeight: '78vh' }}
          />
        ) : (
          <img
            src={blobUrl}
            alt={att.file_name}
            className="rounded-lg object-contain"
            style={{ maxWidth: '85vw', maxHeight: '78vh' }}
          />
        )}

        {/* Footer bar */}
        <div className="flex items-center justify-between gap-6 mt-4 w-full px-1">
          <span className="text-white/60 text-sm truncate">{att.file_name}</span>
          <button
            onClick={handleDownload}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors"
            style={{ background: 'rgba(255,255,255,0.15)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
          >
            <Download size={14} /> Download
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Attachment thumbnail + lightbox trigger ──────────────────────────────────

function AttachmentPreview({ att }: { att: AttachmentMeta }) {
  const isImage = att.mime_type.startsWith('image/');
  const isVideo = att.mime_type.startsWith('video/');
  const isMedia = isImage || isVideo;

  const [thumbSrc, setThumbSrc]   = useState<string | null>(null);
  const [lightbox, setLightbox]   = useState(false);
  const [blobUrl, setBlobUrl]     = useState<string | null>(null);
  const [loading, setLoading]     = useState(false);

  // Pre-fetch image thumbnails; videos show a static placeholder.
  useEffect(() => {
    if (!isImage) return;
    let url = '';
    api.fetchBlob(`/api/admin/attachments/${att.id}`)
      .then((blob) => {
        if (blob) { url = URL.createObjectURL(blob); setThumbSrc(url); }
      })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id, isImage]);

  async function openLightbox() {
    if (!isMedia) return;
    setLightbox(true);
    // Images: reuse thumb blob if already loaded.
    if (thumbSrc) { setBlobUrl(thumbSrc); return; }
    // Videos (or image not yet loaded): fetch on demand.
    setLoading(true);
    const blob = await api.fetchBlob(`/api/admin/attachments/${att.id}`).catch(() => null);
    if (blob) setBlobUrl(URL.createObjectURL(blob));
    setLoading(false);
  }

  function closeLightbox() {
    setLightbox(false);
    // Only revoke if it's a separately-fetched URL (video / fallback), not the thumb.
    if (blobUrl && blobUrl !== thumbSrc) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
  }

  async function handleDownload() {
    const blob = await api.fetchBlob(`/api/admin/attachments/${att.id}`).catch(() => null);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = att.file_name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Media thumbnail (image or video)
  if (isMedia) {
    return (
      <>
        <div
          className="rounded-lg overflow-hidden border border-gray-200 hover:border-brand-400 transition-colors cursor-pointer"
          style={{ maxWidth: '220px' }}
          onClick={openLightbox}
          title="Click to preview"
        >
          {isImage ? (
            thumbSrc
              ? <img src={thumbSrc} alt={att.file_name} className="w-full object-cover" style={{ maxHeight: '140px' }} />
              : <div className="w-full bg-gray-50 flex items-center justify-center text-gray-400 text-xs" style={{ height: '80px' }}>Loading…</div>
          ) : (
            /* Video placeholder */
            <div className="w-full bg-gray-800 flex items-center justify-center" style={{ height: '80px' }}>
              <Play size={28} className="text-white/70" />
            </div>
          )}
          <div className="flex items-center justify-between px-2 py-1.5 gap-2">
            <span className="text-xs text-gray-400 truncate">{att.file_name}</span>
            <button
              onClick={(e) => { e.stopPropagation(); handleDownload(); }}
              className="flex-shrink-0 text-gray-400 hover:text-brand-600 transition-colors"
              title="Download"
            >
              <Download size={13} />
            </button>
          </div>
        </div>

        {lightbox && (
          loading || !blobUrl
            ? (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={closeLightbox}>
                <div className="text-white text-sm">Loading…</div>
              </div>
            ) : (
              <MediaLightbox att={att} blobUrl={blobUrl} onClose={closeLightbox} />
            )
        )}
      </>
    );
  }

  // ── Non-media file chip
  return (
    <button
      onClick={handleDownload}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs text-gray-700 transition-colors"
    >
      <Paperclip size={12} className="flex-shrink-0" />
      <span className="truncate" style={{ maxWidth: '180px' }}>{att.file_name}</span>
      <span className="text-gray-400 flex-shrink-0">{formatBytes(att.file_size)}</span>
    </button>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket]   = useState<AdminTicket | null>(null);
  const [threads, setThreads] = useState<AdminTicketThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const [reply, setReply]           = useState('');
  const [replyFiles, setReplyFiles] = useState<ReplyAttachment[]>([]);
  const [sending, setSending]       = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  const [statusChanging, setStatusChanging] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  async function loadDetail() {
    if (!id) return;
    setError(null);
    try {
      const res = await api.getAdminTicket(Number(id));
      setTicket(res.ticket);
      setThreads(res.threads);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load ticket.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadDetail(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [threads]);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    for (const file of files) {
      if (!ALLOWED_MIME[file.type]) {
        setReplyError(`"${file.name}" is not supported.`);
        continue;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setReplyFiles((prev) => [...prev, { name: file.name, type: file.type, data: base64 }]);
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeReplyFile(idx: number) {
    setReplyFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  async function sendReply(e: FormEvent) {
    e.preventDefault();
    const text = reply.trim();
    if (!text || sending || !ticket) return;
    setSending(true);
    setReplyError(null);
    try {
      const res = await api.adminReplyTicket(
        ticket.id,
        text,
        replyFiles.length > 0 ? replyFiles : undefined,
      );
      setReply('');
      setReplyFiles([]);
      setThreads((prev) => [...prev, {
        id: res.reply_id,
        ticket_id: ticket.id,
        created_by: 'agent',
        author_name: 'You',
        body: text,
        created_at: res.created_at,
        attachments: [],
      }]);
      // Re-fetch to sync ticket updated_at and attachment metadata
      loadDetail();
    } catch (e) {
      setReplyError(e instanceof ApiClientError ? e.message : 'Failed to send reply.');
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(newStatus: TicketStatus) {
    if (!ticket || statusChanging) return;
    setStatusChanging(true);
    try {
      await api.setTicketStatus(ticket.id, newStatus);
      setTicket((t) => t ? { ...t, status: newStatus } : t);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to update status.');
    } finally {
      setStatusChanging(false);
    }
  }

  // ── Loading / error states ──

  if (loading) {
    return (
      <div className="text-sm text-gray-500 py-16 text-center">Loading ticket…</div>
    );
  }

  if (error && !ticket) {
    return (
      <div className="max-w-3xl">
        <button onClick={() => navigate('/tickets')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft size={14} /> Back to tickets
        </button>
        <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg text-red-800 text-sm">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ticket.status === 'closed' || ticket.status === 'resolved';

  return (
    <div className="max-w-3xl flex flex-col md:h-[calc(100vh-64px)]">

      {/* ── Header ── */}
      <div className="flex-shrink-0 mb-4">
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
        >
          <ArrowLeft size={14} /> Back to tickets
        </button>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{ticket.subject}</h2>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                <span>Ticket #{ticket.id}</span>
                <span>·</span>
                <span>
                  <span className="font-medium text-gray-700">{ticket.user_name || 'Unknown'}</span>
                  {ticket.user_email && <> · <a href={`mailto:${ticket.user_email}`} className="text-brand-600 hover:underline">{ticket.user_email}</a></>}
                </span>
                <span>·</span>
                <span>Tenant: <span className="font-mono">{ticket.tenant_id}</span></span>
                <span>·</span>
                <span>Opened {relativeTime(ticket.created_at)}</span>
              </div>
            </div>

            {/* Status selector */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[ticket.status] ?? STATUS_BADGE.open}`}>
                {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
              </span>
              <select
                value={ticket.status}
                onChange={(e) => changeStatus(e.target.value as TicketStatus)}
                disabled={statusChanging}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 mt-3 text-xs text-red-600">
              <AlertCircle size={13} /> {error}
            </div>
          )}
        </div>
      </div>

      {/* ── Thread list ── */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto bg-white rounded-xl border border-gray-200 p-5 space-y-4 mb-4"
      >
        {threads.length === 0 && (
          <div className="text-sm text-gray-400 text-center py-8">No messages yet.</div>
        )}

        {threads.map((t) => {
          const isAgent = t.created_by === 'agent';
          const display = t.body.includes('<') ? stripHtml(t.body) : t.body;
          return (
            <div key={t.id} className={`flex gap-3 ${isAgent ? 'flex-row-reverse' : ''}`}>
              {/* Avatar */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white ${isAgent ? 'bg-brand-600' : 'bg-gray-400'}`}>
                {isAgent ? <Headphones size={14} /> : <User size={14} />}
              </div>
              {/* Bubble */}
              <div className={`max-w-[78%] ${isAgent ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                <div className="text-xs text-gray-400">
                  {isAgent ? 'Support Agent' : t.author_name} · {relativeTime(t.created_at)}
                </div>
                <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                  isAgent
                    ? 'bg-brand-600 text-white rounded-tr-sm'
                    : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                }`}>
                  {display}
                </div>
                {/* Attachments */}
                {t.attachments && t.attachments.length > 0 && (
                  <div className={`flex flex-col gap-2 mt-1 ${isAgent ? 'items-end' : 'items-start'}`}>
                    {t.attachments.map((att) => (
                      <AttachmentPreview key={att.id} att={att} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Reply form ── */}
      {isClosed ? (
        <div className="flex-shrink-0 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4 text-sm text-gray-500 text-center">
          This ticket is {ticket.status} — reopen it to send a reply.
        </div>
      ) : (
        <form onSubmit={sendReply} className="flex-shrink-0 bg-white border border-gray-200 rounded-xl p-4">
          {replyError && (
            <div className="flex items-center gap-2 mb-3 text-xs text-red-600">
              <AlertCircle size={13} /> {replyError}
            </div>
          )}

          {/* Pending attachment chips */}
          {replyFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {replyFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-700">
                  <Paperclip size={11} />
                  <span className="max-w-[140px] truncate">{f.name}</span>
                  <button type="button" onClick={() => removeReplyFile(i)} className="text-gray-400 hover:text-gray-600">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 items-end">
            {/* File attach button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach file"
              className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <Paperclip size={14} />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.xls,.xlsx,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />

            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendReply(e as unknown as FormEvent);
                }
              }}
              disabled={sending}
              placeholder="Type your reply… (Enter to send, Shift+Enter for new line)"
              rows={3}
              className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={sending || !reply.trim()}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={14} />
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
